import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import NewsListItem from "@/components/feature/NewsListItem";
import StateBlock from "@/components/feature/StateBlock";
import { ListSkeleton } from "@/components/feature/NewsSkeleton";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useMockLoading } from "@/hooks/useMockLoading";
import { getHotNews, searchNews } from "@/lib/news";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const { loading, reload } = useMockLoading(450);

  useDocumentMeta({
    title: keyword ? `“${keyword}”搜索结果_91爆料` : "搜索新闻_91爆料",
    description: keyword
      ? `91爆料站内搜索“${keyword}”的相关热点新闻与独家爆料报道，为你呈现最新最热的内容。`
      : "在91爆料搜索最新热点新闻与独家爆料，快速筛选你关注的内容领域。",
    keywords: `91爆料,搜索新闻,${keyword || "热点新闻"}`,
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => searchNews(keyword), [keyword]);
  const hotNews = getHotNews(5);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = input.trim();
    setParams(kw ? { q: kw } : {});
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
      {/* 搜索区 */}
      <section className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h1 className="font-heading text-2xl font-black text-foreground-900 md:text-3xl">搜索新闻</h1>
        <p className="mt-2 text-sm text-foreground-500">输入关键词，快速查找你关注的热点新闻与独家爆料（演示数据）</p>
        <form onSubmit={submit} className="mt-5 flex flex-col gap-3 sm:flex-row">
          <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-100 px-4 py-3 transition-colors focus-within:border-primary-400">
            <i className="ri-search-line text-foreground-400"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              type="search"
              placeholder="例如：AI、暴雨、综艺、股市"
              className="w-full bg-transparent text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
            />
          </div>
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            搜索
          </button>
        </form>
      </section>

      {/* 结果区 */}
      {keyword ? (
        <section className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-foreground-600">
              搜索“<span className="font-bold text-primary-600">{keyword}</span>”共找到{" "}
              <span className="font-bold text-accent-600">{results.length}</span> 条结果
            </p>
            <button
              type="button"
              onClick={reload}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm text-foreground-500 transition-colors hover:text-primary-600"
            >
              <i className="ri-refresh-line"></i>
              重新搜索
            </button>
          </div>

          <div className="mt-4 rounded-lg border border-background-200 bg-background-50 px-5">
            {loading ? (
              <ListSkeleton count={4} />
            ) : results.length === 0 ? (
              <div className="py-8">
                <StateBlock
                  icon="ri-emotion-sad-line"
                  title={`没有找到与“${keyword}”相关的新闻`}
                  description="换个关键词试试，或者直接浏览下方的热门推荐内容。"
                />
              </div>
            ) : (
              <div>
                {results.map((news) => (
                  <NewsListItem key={news.id} news={news} />
                ))}
              </div>
            )}
          </div>
        </section>
      ) : (
        <section className="mt-6">
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
              <i className="ri-search-2-line text-3xl"></i>
            </span>
            <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">开始搜索你关注的新闻</h2>
            <p className="mt-2 text-sm text-foreground-500">试试上方输入框，或从下方热门关键词快速进入。</p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {["AI", "暴雨", "综艺", "股市", "新能源", "国足"].map((kw) => (
                <Link
                  key={kw}
                  to={`/search?q=${encodeURIComponent(kw)}`}
                  className="cursor-pointer whitespace-nowrap rounded-full bg-background-100 px-4 py-1.5 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-600"
                >
                  {kw}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 热门推荐 */}
      <section className="mt-8">
        <div className="mb-4 flex items-center gap-2">
          <span className="flex h-6 w-1.5 rounded-full bg-accent-500"></span>
          <h2 className="font-heading text-xl font-bold text-foreground-900">热门推荐</h2>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 px-5">
          {hotNews.map((news) => (
            <NewsListItem key={news.id} news={news} />
          ))}
        </div>
      </section>
    </div>
  );
}